import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { GestionDeCurso } from './gestion/GestionDeCurso';
import { Leccion } from './Leccion';
import { Opinion, ReportarProblema } from './Dialogos';
import { Quiz } from './Quiz';
import { IconoDePortada, MODALIDADES, NIVELES, TIPOS } from './piezas';
import {
  useCertificados, useCurso, useEstadoEditorial, useInscribirse, useRecertificarse,
  type CursoDetalle, type Leccion as DatosDeLeccion,
} from './api';

/**
 * DetalleDeCurso — `#course-detail/<id>`. Módulo 11, opción 2.
 *
 * El archivo vanilla tenía 1.691 líneas y dos mitades: lo que hace quien
 * **aprende** —inscribirse, ver lecciones, el quiz, opinar, el certificado— y
 * el panel de quien **gestiona**. Esta pantalla es la primera; la segunda vive
 * en `gestion/`.
 *
 * ## Lo que estaba roto, y no se veía en un escritorio
 *
 * **En un teléfono el reproductor medía 19 px.** El temario y el reproductor se
 * repartían la pantalla con `grid-template-columns: 300px 1fr` escrito en línea,
 * sin ningún corte: a 375 px, menos el relleno de la página, quedan 343; el
 * temario se lleva 300 y el hueco 24, así que el vídeo tenía **19 px** de ancho.
 * Aquí los dos se apilan por debajo de 1024 px.
 *
 * **Un fallo al cargar era un callejón sin salida**: un `alert-error` con el
 * mensaje y nada más —ni reintentar ni volver—.
 *
 * **Descargar el certificado podía no hacer nada**, en silencio: el vanilla
 * pedía `/certificates/my` al pulsar y abría el PDF con `window.open` después
 * del `await`, que un bloqueador de ventanas corta. Ver `useCertificados`.
 *
 * ## El quiz ya no sustituye la página entera
 *
 * Se pintaba con `page-container.innerHTML = …`, borrando el curso. Aquí es una
 * vista de la misma pantalla: «Volver al curso» no vuelve a pedir nada que ya
 * esté en caché, y el resultado actualiza el avance sin recargar.
 */

/** El mínimo del servidor (`QUIZ_APROBACION_PCT`). El resultado trae el de
 *  verdad en `passing_score`; esto solo se usa antes de presentarlo. */
const APROBACION_POR_DEFECTO = 70;

export function DetalleDeCurso() {
  // La sub-ruta se lee UNA vez: el router desmonta y vuelve a montar la capa
  // React en cada `hashchange`, así que otro curso ya es otro montaje.
  const [id] = useState(() => {
    const p = window._routeParams?.[0];
    return p && /^\d+$/.test(p) ? Number(p) : null;
  });

  if (id == null) {
    return (
      <EstadoError
        err={new Error('No se indicó qué curso abrir.')}
        que="el curso"
        onVolver={() => APP.navigate('courses')}
      />
    );
  }
  return <Curso id={id} />;
}

function Curso({ id }: { id: number }) {
  const puedeGestionar = Auth.can('cursos', 'manage') || Auth.isStaff();
  const curso = useCurso(id);
  const [vista, setVista] = useState<'curso' | 'quiz'>('curso');
  const [abierta, setAbierta] = useState<number | null>(null);
  const [gestionando, setGestionando] = useState(false);
  const [opinando, setOpinando] = useState(false);
  const [reportando, setReportando] = useState<{ leccionId: number | null } | null>(null);

  if (curso.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-52 w-full" />
        <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      </div>
    );
  }

  if (curso.isError || !curso.data) {
    return (
      <EstadoError
        err={curso.error}
        que="el curso"
        onReintentar={() => void curso.refetch()}
        onVolver={() => APP.navigate('courses')}
      />
    );
  }

  const co = curso.data;

  if (vista === 'quiz') {
    return <Quiz curso={co} onVolver={() => setVista('curso')} />;
  }

  const leccion = co.lessons.find((l) => l.id === abierta) ?? null;

  return (
    <>
      <BarraSuperior co={co} puedeGestionar={puedeGestionar} onGestionar={() => setGestionando(true)} />

      {co.paths.length > 0 && <ContextoDeRuta co={co} />}

      <Cabecera co={co} puedeGestionar={puedeGestionar} onOpinar={() => setOpinando(true)} />

      <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
        <Temario co={co} abierta={abierta} onAbrir={setAbierta} onPresentarQuiz={() => setVista('quiz')} />

        <div className="min-w-0">
          {leccion ? (
            <Leccion
              curso={co}
              leccion={leccion}
              onReportar={() => setReportando({ leccionId: leccion.id })}
            />
          ) : (
            <SinLeccion co={co} />
          )}
          {!leccion && (
            <div className="mt-3 flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setReportando({ leccionId: null })}>
                <i className="bi bi-exclamation-triangle" aria-hidden="true" /> Reportar un problema
              </Button>
            </div>
          )}
        </div>
      </div>

      {co.enrolled && <Opinion curso={co} abierto={opinando} onCerrar={() => setOpinando(false)} />}
      <ReportarProblema
        curso={co}
        leccionId={reportando?.leccionId ?? null}
        abierto={reportando != null}
        onCerrar={() => setReportando(null)}
      />
      {puedeGestionar && (
        <GestionDeCurso curso={co} abierto={gestionando} onCerrar={() => setGestionando(false)} />
      )}
    </>
  );
}

/* ── La barra de arriba ────────────────────────────────────────────────── */

function BarraSuperior({
  co, puedeGestionar, onGestionar,
}: {
  co: CursoDetalle;
  puedeGestionar: boolean;
  onGestionar: () => void;
}) {
  const tipo = TIPOS[co.course_type] ?? TIPOS.curso!;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {/* Enlace y no botón: `APP.navigate` usa `replaceState` y el Atrás del
          navegador se saltaría el catálogo. */}
      <a
        href="#cursos"
        className="inline-flex min-h-11 items-center gap-1.5 rounded-sm px-2 text-3 text-ink-2
                   no-underline hover:text-brand-ink focus-visible:focus-anillo"
      >
        <i className="bi bi-arrow-left" aria-hidden="true" /> Cursos
      </a>
      <Badge>
        <i className={cn('bi', tipo.icono)} aria-hidden="true" /> {tipo.etiqueta}
      </Badge>
      {(co.assignment_mandatory || co.is_mandatory) && <Badge tone="danger">Obligatorio</Badge>}
      <ChipDeAsignacion co={co} />
      {puedeGestionar && (
        <Button variant="secondary" size="sm" className="ml-auto" onClick={onGestionar}>
          <i className="bi bi-pencil-square" aria-hidden="true" /> Gestionar curso
        </Button>
      )}
    </div>
  );
}

/** El semáforo de una asignación formal, para quien la recibió. */
function ChipDeAsignacion({ co }: { co: CursoDetalle }) {
  if (!co.assigned || co.completed) return null;
  const meta = {
    vencido: { tono: 'danger' as const, icono: 'bi-exclamation-circle', texto: 'Vencida' },
    proximo: { tono: 'warn' as const, icono: 'bi-clock-history', texto: 'Por vencer' },
    al_dia: { tono: 'info' as const, icono: 'bi-calendar-check', texto: 'Asignada' },
  }[co.assignment_status ?? 'al_dia'];
  return (
    <Badge tone={meta.tono}>
      <i className={cn('bi', meta.icono)} aria-hidden="true" /> {meta.texto}
      {co.assignment_due ? ` · vence ${Utils.fmtDateOnly(co.assignment_due)}` : ''}
    </Badge>
  );
}

/* ── En qué ruta va ────────────────────────────────────────────────────── */

/**
 * Quien entraba a un curso desde una ruta perdía el hilo: la pantalla no decía
 * que formaba parte de una secuencia ni qué tocaba después.
 *
 * En el vanilla iba con un filete de 3 px en navy a la izquierda. Aquí es una
 * superficie plana con su línea de 1 px, la decisión del 2026-09-01: el tono y
 * la línea separan, no un filete de color en un borde.
 */
function ContextoDeRuta({ co }: { co: CursoDetalle }) {
  return (
    <ul className="mb-4 flex flex-col gap-2">
      {co.paths.map((r) => {
        const pct = r.required_count ? Math.round((r.completed_count / r.required_count) * 100) : 0;
        return (
          <li
            key={r.path_id}
            className="flex flex-wrap items-center gap-3 rounded-sm border border-line bg-sunken px-3.5 py-2.5"
          >
            <i className="bi bi-signpost-split text-brand-ink" aria-hidden="true" />
            <div className="min-w-44 flex-1">
              <p className="text-2 text-muted">
                Paso <strong className="text-ink-2">{r.step}</strong> de{' '}
                <strong className="text-ink-2">{r.total}</strong> en la ruta
                {r.is_mandatory && <Badge tone="danger" className="ml-1.5">Obligatoria</Badge>}
              </p>
              <p className="text-3">
                <a
                  href={`#rutas/${r.path_id}`}
                  className="font-semibold text-brand-ink underline-offset-2 hover:underline focus-visible:focus-anillo"
                >
                  {r.title}
                </a>
                <span className="ml-1.5 text-1 text-muted">
                  · {r.completed_count} de {r.required_count} completados ({pct} %)
                </span>
              </p>
            </div>
            {r.next_course ? (
              <a
                href={`#course-detail/${r.next_course.course_id}`}
                className="inline-flex min-h-8 items-center gap-1 rounded-sm border border-brand-ink px-3
                           text-2 font-semibold text-brand-ink no-underline hover:bg-brand hover:text-on-brand
                           pointer-coarse:min-h-11 focus-visible:focus-anillo"
              >
                Siguiente: {r.next_course.title}{' '}
                <i className="bi bi-arrow-right-short" aria-hidden="true" />
              </a>
            ) : r.step < r.total ? (
              <span className="flex items-center gap-1 text-2 text-muted">
                <i className="bi bi-lock" aria-hidden="true" /> Completa este curso para seguir
              </span>
            ) : (
              <span className="flex items-center gap-1 text-2 text-ok-ink">
                <i className="bi bi-flag-fill" aria-hidden="true" /> Último paso
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ── La cabecera ───────────────────────────────────────────────────────── */

function vigencia(meses: number) {
  if (meses >= 12) {
    const anios = meses / 12;
    return `${anios} ${anios === 1 ? 'año' : 'años'}`;
  }
  return `${meses} ${meses === 1 ? 'mes' : 'meses'}`;
}

function Cabecera({
  co, puedeGestionar, onOpinar,
}: {
  co: CursoDetalle;
  puedeGestionar: boolean;
  onOpinar: () => void;
}) {
  const inscribirse = useInscribirse();
  const estado = useEstadoEditorial();
  const [archivando, setArchivando] = useState(false);
  const pct = Math.round(co.progress_pct || 0);

  const cambiarEstado = (status: 'publicado' | 'archivado') =>
    estado.mutate(
      { cursoId: co.id, status },
      {
        onSuccess: () => {
          setArchivando(false);
          Utils.toast(status === 'publicado' ? 'Curso publicado' : 'Curso archivado', 'success');
        },
        onError: (err) => {
          setArchivando(false);
          Utils.toast(err instanceof Error ? err.message : 'No se pudo cambiar el estado', 'error');
        },
      },
    );

  return (
    <section className="mb-5 overflow-hidden rounded-md border border-line bg-surface">
      <div
        className="flex h-28 items-center justify-center overflow-hidden"
        style={{ background: co.cover_icon_color || '#0D2B4E' }}
        aria-hidden="true"
      >
        {co.cover_url ? (
          <img src={co.cover_url} alt="" className="size-full object-cover" />
        ) : (
          <IconoDePortada valor={co.cover_icon} respaldo="bi-journal-bookmark" className="text-9 leading-none text-white" />
        )}
      </div>

      <div className="flex flex-col gap-3 p-4">
        <h1 className="text-7 font-bold leading-tight text-brand-ink [overflow-wrap:anywhere]">{co.title}</h1>

        {puedeGestionar && co.status && co.status !== 'publicado' && (
          <p className="flex items-center gap-2 rounded-sm border border-line bg-sunken px-3 py-2 text-2 text-ink-2">
            <i
              className={cn('bi', co.status === 'borrador' ? 'bi-pencil-square' : 'bi-archive', 'text-warn-ink')}
              aria-hidden="true"
            />
            Este curso está en <strong>{co.status}</strong>
            {co.status === 'borrador' ? ' — los colaboradores aún no lo ven.' : '.'}
          </p>
        )}

        {co.description && (
          <p className="text-3 text-ink-2 [overflow-wrap:anywhere]">{co.description}</p>
        )}

        {/* Superficie plana y no el filete naranja de 3 px que llevaba: era uno
            de los tres filetes decorativos que quedaban en el portal, anotado
            desde el 2026-09-08. El acento no decora cajas de texto. */}
        {co.objective && (
          <div className="rounded-sm border border-line bg-sunken px-3 py-2.5">
            <p className="text-1 font-bold uppercase tracking-wide text-muted">Objetivo</p>
            <p className="mt-0.5 text-3 text-ink-2 [overflow-wrap:anywhere]">{co.objective}</p>
          </div>
        )}

        {(co.level || co.modality || co.validity_months) && (
          <div className="flex flex-wrap gap-2">
            {co.level && (
              <Badge tone="info">
                <i className="bi bi-bar-chart-steps" aria-hidden="true" /> {NIVELES[co.level] ?? co.level}
              </Badge>
            )}
            {co.modality && (
              <Badge>
                <i className="bi bi-easel" aria-hidden="true" /> {MODALIDADES[co.modality] ?? co.modality}
              </Badge>
            )}
            {co.validity_months ? (
              <Badge tone="ok">
                <i className="bi bi-patch-check" aria-hidden="true" /> Certificado válido{' '}
                {vigencia(co.validity_months)}
              </Badge>
            ) : null}
          </div>
        )}

        {puedeGestionar && (
          <div className="flex flex-wrap gap-2">
            {co.status !== 'publicado' && (
              <Button size="sm" loading={estado.isPending} onClick={() => cambiarEstado('publicado')}>
                <i className="bi bi-broadcast" aria-hidden="true" />{' '}
                {co.status === 'archivado' ? 'Restaurar' : 'Publicar'}
              </Button>
            )}
            {co.status !== 'archivado' && (
              <Button variant="secondary" size="sm" onClick={() => setArchivando(true)}>
                <i className="bi bi-archive" aria-hidden="true" /> Archivar
              </Button>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <span className="flex items-center gap-1.5 text-3 text-muted">
            <i className="bi bi-collection-play" aria-hidden="true" />
            {co.lesson_count} {co.lesson_count === 1 ? 'lección' : 'lecciones'}
          </span>
          {co.enrolled ? (
            <>
              <EstadoDeInscripcion co={co} />
              <Button variant="secondary" size="sm" onClick={onOpinar}>
                <i className="bi bi-chat-heart" aria-hidden="true" /> Opinar sobre el curso
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              loading={inscribirse.isPending}
              onClick={() =>
                inscribirse.mutate(co.id, {
                  onSuccess: () => Utils.toast('Te inscribiste en el curso', 'success'),
                  onError: (err) =>
                    Utils.toast(err instanceof Error ? err.message : 'No se pudo inscribir', 'error'),
                })
              }
            >
              <i className="bi bi-play-circle-fill" aria-hidden="true" /> Inscribirme
            </Button>
          )}
        </div>

        {co.enrolled && <BarraDeProgreso pct={pct} etiqueta="Avance del curso" />}
      </div>

      <ConfirmDialog
        abierto={archivando}
        onCambio={(v) => { if (!v) setArchivando(false); }}
        titulo="Archivar el curso"
        descripcion="Dejará de verse en el catálogo. Quien ya lo completó conserva su certificado."
        textoConfirmar="Archivar"
        destructivo
        cargando={estado.isPending}
        onConfirmar={() => cambiarEstado('archivado')}
      />
    </section>
  );
}

/**
 * El certificado de quien mira, en sus tres momentos.
 *
 * `/retake` reinicia el avance pero **conserva** el certificado vencido hasta que
 * se renueve. El vanilla solo miraba `has_certificate`, así que tras
 * reiniciar seguía diciendo «Curso completado», el pie seguía ofreciendo
 * «Recertificarme» y **el quiz no volvía a salir nunca**: en un curso con quiz,
 * renovar el certificado era imposible desde la pantalla. Lo que distingue los
 * dos momentos es `completed` —`completed_at`—, que `/retake` pone a nulo.
 */
function momentoDelCertificado(co: CursoDetalle): 'vigente' | 'vencido' | 'renovando' | null {
  if (!co.has_certificate) return null;
  if (co.cert_status !== 'vencido') return 'vigente';
  return co.completed ? 'vencido' : 'renovando';
}

/** Distingue «quiz pendiente» —lecciones al 100 % pero sin aprobar— de «curso
 *  completado», y los dos de un certificado vencido o en renovación. */
function EstadoDeInscripcion({ co }: { co: CursoDetalle }) {
  const momento = momentoDelCertificado(co);
  if (momento === 'vencido') {
    return (
      <span className="flex items-center gap-1.5 text-3 text-danger-ink">
        <i className="bi bi-exclamation-octagon-fill" aria-hidden="true" /> Completado · el certificado venció
      </span>
    );
  }
  if (momento === 'vigente' || co.completed) {
    return (
      <span className="flex items-center gap-1.5 text-3 text-ok-ink">
        <i className="bi bi-patch-check-fill" aria-hidden="true" /> Curso completado
      </span>
    );
  }
  if (co.has_quiz && co.progress_pct >= 100 && !co.quiz_passed) {
    return (
      <span className="flex items-center gap-1.5 text-3 text-warn-ink">
        <i className="bi bi-hourglass-split" aria-hidden="true" /> Lecciones completas · falta el quiz
      </span>
    );
  }
  if (momento === 'renovando') {
    return (
      <span className="flex items-center gap-1.5 text-3 text-ink-2">
        <i className="bi bi-arrow-repeat text-brand-ink" aria-hidden="true" /> Renovando el certificado ·{' '}
        {Math.round(co.progress_pct)} % completado
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-3 text-ink-2">
      <i className="bi bi-check-circle-fill text-ok-ink" aria-hidden="true" /> Inscrito ·{' '}
      {Math.round(co.progress_pct)} % completado
    </span>
  );
}

/* ── El temario ────────────────────────────────────────────────────────── */

function Temario({
  co, abierta, onAbrir, onPresentarQuiz,
}: {
  co: CursoDetalle;
  abierta: number | null;
  onAbrir: (id: number) => void;
  onPresentarQuiz: () => void;
}) {
  const lecciones = co.lessons;
  const hechas = lecciones.filter((l) => l.completed).length;
  const restan = lecciones.filter((l) => !l.completed).reduce((s, l) => s + (l.duration_minutes || 0), 0);
  // Dónde retomar: la primera sin completar. No hay forma de saber si una
  // lección está «empezada» —`watch_seconds` existe en la tabla y no la escribe
  // nadie—, así que lo que se ofrece es por dónde sigue.
  const siguiente = lecciones.findIndex((l) => !l.completed);

  return (
    <aside
      aria-labelledby="temario-titulo"
      className="h-fit overflow-hidden rounded-md border border-line bg-surface"
    >
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 id="temario-titulo" className="flex items-center gap-1.5 text-3 font-bold text-brand-ink">
          <i className="bi bi-list-ul" aria-hidden="true" /> Lecciones
        </h2>
        {lecciones.length > 0 && (
          <span className="text-2 tabular-nums text-muted">
            {hechas} / {lecciones.length}
            {restan ? ` · quedan ~${restan} min` : ''}
          </span>
        )}
      </div>

      {lecciones.length ? (
        <ol>
          {lecciones.map((l, i) => (
            <li key={l.id}>
              <FilaDeLeccion
                l={l}
                i={i}
                total={lecciones.length}
                esSiguiente={i === siguiente}
                activa={l.id === abierta}
                inscrito={co.enrolled}
                onAbrir={() => onAbrir(l.id)}
              />
            </li>
          ))}
        </ol>
      ) : (
        <p className="px-4 py-6 text-center text-3 text-muted">
          <i className="bi bi-inbox mb-1 block text-7" aria-hidden="true" />
          Este curso aún no tiene lecciones.
        </p>
      )}

      <PieDelTemario co={co} onPresentarQuiz={onPresentarQuiz} />
    </aside>
  );
}

function FilaDeLeccion({
  l, i, total, esSiguiente, activa, inscrito, onAbrir,
}: {
  l: DatosDeLeccion;
  i: number;
  total: number;
  esSiguiente: boolean;
  activa: boolean;
  inscrito: boolean;
  onAbrir: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!inscrito) {
          Utils.toast('Inscríbete primero para ver las lecciones', 'warning');
          return;
        }
        onAbrir();
      }}
      aria-current={activa ? 'true' : undefined}
      aria-label={`Lección ${i + 1} de ${total}: ${l.title}${
        l.completed ? ' (completada)' : esSiguiente ? ' (continúa aquí)' : ''}`}
      className={cn(
        'flex w-full items-center gap-3 border-b border-line px-4 py-3 text-left text-inherit',
        'last:border-0 hover:bg-sunken focus-visible:focus-anillo',
        activa && 'bg-brand/[.06]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full text-2 font-bold',
          l.completed ? 'bg-ok text-white' : esSiguiente ? 'bg-brand text-on-brand' : 'bg-sunken text-ink-2',
        )}
      >
        {l.completed ? <i className="bi bi-check-lg" /> : i + 1}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-3', l.completed ? 'font-semibold' : 'font-medium')}>
          {l.title}
        </span>
        <span className="flex flex-wrap items-center gap-1.5 text-2 text-muted">
          <i
            className={cn('bi', l.video_type === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-youtube')}
            aria-hidden="true"
          />
          {l.video_type === 'pdf' ? 'PDF' : 'Video'}
          {l.duration_minutes ? ` · ${l.duration_minutes} min` : ''}
          {esSiguiente && (
            <Badge tone="info" className="ml-0.5">Continúa aquí</Badge>
          )}
        </span>
      </span>
    </button>
  );
}

/**
 * Lo que va al pie del temario: el certificado, el quiz o la recertificación.
 *
 * El certificado solo se ofrece si **está emitido** —quiz aprobado, o curso sin
 * quiz completado—. Con el quiz pendiente no se ofrece, igual que antes.
 */
function PieDelTemario({ co, onPresentarQuiz }: { co: CursoDetalle; onPresentarQuiz: () => void }) {
  const certificados = useCertificados(co.enrolled && co.has_certificate);
  const recertificarse = useRecertificarse();
  const [confirmando, setConfirmando] = useState(false);

  if (!co.enrolled) return null;

  const enlace = certificados.data?.find((c) => c.course_id === co.id)?.download_url;

  const Descargar = ({ texto, variante }: { texto: string; variante: 'primario' | 'fantasma' }) =>
    certificados.isLoading ? (
      <Skeleton className="h-10 w-full" />
    ) : enlace ? (
      <a
        href={enlace}
        target="_blank"
        rel="noopener"
        className={cn(
          'flex min-h-10 w-full items-center justify-center gap-2 rounded-sm text-3 font-semibold no-underline',
          'pointer-coarse:min-h-11 focus-visible:focus-anillo',
          variante === 'primario'
            ? 'bg-brand text-on-brand hover:bg-brand-strong'
            : 'text-ink-2 hover:bg-sunken hover:text-brand-ink',
        )}
      >
        <i className="bi bi-award-fill" aria-hidden="true" /> {texto}
      </a>
    ) : (
      <p className="text-center text-2 text-muted">
        {certificados.isError
          ? 'No se pudo consultar tu certificado. Recarga la página para intentarlo otra vez.'
          : 'Tu certificado se está generando; aparecerá aquí en unos segundos.'}
      </p>
    );

  const momento = momentoDelCertificado(co);

  if (momento === 'vencido') {
    return (
      <div className="flex flex-col gap-2 border-t border-line p-4">
        <p className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-2.5 text-2 text-ink-2">
          <i className="bi bi-exclamation-octagon mt-0.5 text-danger-ink" aria-hidden="true" />
          Tu certificado venció
          {co.cert_expires_at ? ` el ${Utils.fmtDate(co.cert_expires_at)}` : ''}. Recertifícate para
          mantenerlo vigente.
        </p>
        <Button block onClick={() => setConfirmando(true)}>
          <i className="bi bi-arrow-repeat" aria-hidden="true" /> Recertificarme
        </Button>
        <Descargar texto="Ver el certificado vencido" variante="fantasma" />
        <ConfirmDialog
          abierto={confirmando}
          onCambio={(v) => { if (!v) setConfirmando(false); }}
          titulo="Recertificarme"
          descripcion={
            co.has_quiz
              ? 'Volverás a tomar el curso desde cero: al completar las lecciones y aprobar el quiz, se emite de nuevo.'
              : 'Volverás a tomar el curso desde cero: al completar las lecciones, se emite de nuevo.'
          }
          textoConfirmar="Empezar de nuevo"
          cargando={recertificarse.isPending}
          onConfirmar={() =>
            recertificarse.mutate(co.id, {
              onSuccess: () => {
                setConfirmando(false);
                Utils.toast('Curso reiniciado: complétalo para renovar el certificado', 'success');
              },
              onError: (err) => {
                setConfirmando(false);
                Utils.toast(err instanceof Error ? err.message : 'No se pudo reiniciar', 'error');
              },
            })
          }
        />
      </div>
    );
  }

  if (momento === 'vigente') {
    return (
      <div className="flex flex-col gap-2 border-t border-line p-4">
        <Descargar texto="Descargar certificado" variante="primario" />
        {co.cert_expires_at && (
          <p
            className={cn(
              'flex items-center justify-center gap-1.5 text-2',
              co.cert_status === 'por_vencer' ? 'text-warn-ink' : 'text-muted',
            )}
          >
            <i className={cn('bi', co.cert_status === 'por_vencer' ? 'bi-clock-history' : 'bi-patch-check')} aria-hidden="true" />
            {co.cert_status === 'por_vencer' ? 'Vence el' : 'Válido hasta el'} {Utils.fmtDate(co.cert_expires_at)}
          </p>
        )}
      </div>
    );
  }

  // Sin certificado, o renovándolo tras `/retake`: el camino normal —el quiz
  // cuando toca— más, si se está renovando, el aviso y el certificado viejo.
  const renovando = momento === 'renovando';
  const aviso = renovando && (
    <p className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-2.5 text-2 text-ink-2">
      <i className="bi bi-arrow-repeat mt-0.5 text-brand-ink" aria-hidden="true" />
      Estás renovando tu certificado
      {co.cert_expires_at ? `, que venció el ${Utils.fmtDate(co.cert_expires_at)}` : ''}.{' '}
      {co.has_quiz
        ? 'Completa las lecciones y aprueba el quiz para que se emita de nuevo.'
        : 'Se emite de nuevo al completar las lecciones.'}
    </p>
  );

  if (co.has_quiz) {
    return (
      <div className="flex flex-col gap-2 border-t border-line p-4">
        {aviso}
        {co.progress_pct >= 100 ? (
          <>
            <Button block onClick={onPresentarQuiz}>
              <i className="bi bi-patch-question-fill" aria-hidden="true" /> Presentar el quiz final
            </Button>
            <p className="text-center text-2 text-muted">
              Apruébalo ({APROBACION_POR_DEFECTO} %) para obtener tu certificado.
            </p>
          </>
        ) : (
          <p className="flex items-center justify-center gap-1.5 text-center text-2 text-muted">
            <i className="bi bi-lock-fill" aria-hidden="true" /> Completa todas las lecciones para habilitar el
            quiz final.
          </p>
        )}
        {renovando && <Descargar texto="Ver el certificado vencido" variante="fantasma" />}
      </div>
    );
  }

  if (renovando) {
    return (
      <div className="flex flex-col gap-2 border-t border-line p-4">
        {aviso}
        <Descargar texto="Ver el certificado vencido" variante="fantasma" />
      </div>
    );
  }

  // Sin quiz y sin certificado: se emite al completar las lecciones.
  return null;
}

/* ── Sin lección abierta ───────────────────────────────────────────────── */

function SinLeccion({ co }: { co: CursoDetalle }) {
  const inscribirse = useInscribirse();
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-line bg-surface px-6 py-12 text-center">
      <i className="bi bi-play-circle text-9 text-muted" aria-hidden="true" />
      <p className="text-3 text-muted">
        {!co.lessons.length
          ? 'Este curso aún no tiene lecciones.'
          : co.enrolled
            ? 'Elige una lección del temario para empezar.'
            : 'Inscríbete para ver las lecciones.'}
      </p>
      {!co.enrolled && co.lessons.length > 0 && (
        <Button
          loading={inscribirse.isPending}
          onClick={() =>
            inscribirse.mutate(co.id, {
              onSuccess: () => Utils.toast('Te inscribiste en el curso', 'success'),
              onError: (err) =>
                Utils.toast(err instanceof Error ? err.message : 'No se pudo inscribir', 'error'),
            })
          }
        >
          Inscribirme
        </Button>
      )}
    </div>
  );
}
